/**
 * Avatar Component - HYB Shop Chat
 * Reusable avatar with initials fallback, online status, and guest badge
 *
 * Design: Warm gradient backgrounds with amber/orange accents
 */

import { useMemo } from 'react';
import { User } from 'lucide-react';
import { cn } from '../../../utils/responsive';

interface AvatarProps {
  /** User's display name for generating initials */
  name?: string;
  /** Image URL (optional) */
  imageUrl?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show online status indicator */
  isOnline?: boolean;
  /** Show guest badge */
  isGuest?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// Warm color palette for initials backgrounds
const AVATAR_COLORS = [
  'from-amber-400 to-orange-500',
  'from-orange-400 to-red-500',
  'from-yellow-400 to-amber-500',
  'from-rose-400 to-pink-500',
  'from-teal-400 to-emerald-500',
  'from-blue-400 to-indigo-500',
  'from-purple-400 to-violet-500',
  'from-fuchsia-400 to-pink-500',
];

const SIZE_CONFIG = {
  sm: {
    container: 'h-8 w-8',
    text: 'text-xs',
    icon: 'h-4 w-4',
    statusDot: 'h-2.5 w-2.5 -bottom-0.5 -right-0.5 border-[1.5px]',
    guestBadge: 'text-[8px] px-1 -bottom-1',
  },
  md: {
    container: 'h-10 w-10',
    text: 'text-sm',
    icon: 'h-5 w-5',
    statusDot: 'h-3 w-3 -bottom-0.5 -right-0.5 border-2',
    guestBadge: 'text-[9px] px-1.5 -bottom-1.5',
  },
  lg: {
    container: 'h-12 w-12',
    text: 'text-base',
    icon: 'h-6 w-6',
    statusDot: 'h-3.5 w-3.5 -bottom-1 -right-1 border-2',
    guestBadge: 'text-[10px] px-2 -bottom-2',
  },
};

/**
 * Extract initials from a name
 * Handles Thai and English names
 */
function getInitials(name: string): string {
  if (!name) return '';

  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    // Single word: take first 2 characters
    return words[0].slice(0, 2).toUpperCase();
  }
  // Multiple words: take first character of first two words
  return (words[0][0] + (words[1]?.[0] || '')).toUpperCase();
}

/**
 * Get consistent color based on name
 */
function getColorFromName(name: string): string {
  if (!name) return AVATAR_COLORS[0];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function Avatar({
  name,
  imageUrl,
  size = 'md',
  isOnline,
  isGuest,
  className,
}: AvatarProps) {
  const config = SIZE_CONFIG[size];

  const initials = useMemo(() => getInitials(name || ''), [name]);
  const bgColor = useMemo(() => getColorFromName(name || ''), [name]);

  return (
    <div className={cn('relative inline-flex flex-shrink-0', className)}>
      {/* Main Avatar Circle */}
      <div
        className={cn(
          'relative flex items-center justify-center rounded-full overflow-hidden',
          'ring-2 ring-white dark:ring-gray-800',
          'shadow-sm',
          config.container
        )}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name || 'ผู้ใช้'}
            className="h-full w-full object-cover"
            onError={(e) => {
              // Hide broken image, show initials
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : initials ? (
          <div
            className={cn(
              'h-full w-full flex items-center justify-center',
              'bg-gradient-to-br text-white font-semibold',
              'transition-transform duration-200',
              bgColor,
              config.text
            )}
          >
            {initials}
          </div>
        ) : (
          <div
            className={cn(
              'h-full w-full flex items-center justify-center',
              'bg-gradient-to-br from-stone-200 to-stone-300',
              'dark:from-gray-600 dark:to-gray-700',
              'text-stone-500 dark:text-gray-400'
            )}
          >
            <User className={config.icon} />
          </div>
        )}
      </div>

      {/* Online Status Indicator */}
      {isOnline !== undefined && (
        <span
          className={cn(
            'absolute rounded-full',
            'border-white dark:border-gray-800',
            'transition-colors duration-200',
            isOnline
              ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]'
              : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]',
            config.statusDot
          )}
          title={isOnline ? 'ออนไลน์' : 'ออฟไลน์'}
        />
      )}

      {/* Guest Badge */}
      {isGuest && (
        <span
          className={cn(
            'absolute left-1/2 -translate-x-1/2',
            'bg-stone-600 dark:bg-gray-500 text-white',
            'font-medium rounded-full whitespace-nowrap',
            'shadow-sm',
            config.guestBadge
          )}
        >
          Guest
        </span>
      )}
    </div>
  );
}

export default Avatar;
